/**
 * AdminMyEvents.js — "My Events" account dashboard page.
 *
 * Lists the logged-in user's upcoming and past program bookings,
 * fetched from the engine API. Provides "Add to Calendar" options
 * (Google, Outlook, Yahoo, iCal) and links to event resources.
 * Protected — redirects to home if not logged in.
 *
 * Props:
 *  - Good {string}                Time-of-day greeting.
 *  - EngineURL {string}           Base URL for the engine API.
 *  - useLoggedIn {boolean}        Whether the user is authenticated.
 *  - loggedInUserData {object}    Logged-in user details.
 *  - UpdateCartQty {Function}     Updates the cart badge.
 *  - UpdateCartAmount {Function}  Updates the cart total.
 *  - ShowLoginPopup {Function}    Opens the login modal.
 *  - ShowLoginPopup_ {Function}   Redirects to profile if logged in, else shows login.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { google, outlook, office365, yahoo, ics } from "calendar-link";
import {
    SearchIcon,
    LocationIcon,
    DocumentIcon,
    LoaderIcon,
    GoogleIcon,
    YahooIcon,
    OutlookIcon,
    ICalendarIcon
} from "../../icons";
import DisclaimerSvg from "../../images/disclaimer.svg";
import VideoSvg from "../../images/video.svg";
import LocationSvg from "../../images/location.svg";
import {
    Container,
    Row,
    Col,
    ListGroup,
    Form,
    FormControl,
    Card,
    Modal,
} from 'react-bootstrap';
import axios from 'axios';
import { Helmet } from "react-helmet";
import { AdminSidebar } from './Sidebar/AdminSidebar';
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { CalendarToolbar } from '../Sections/CalendarToolbar';
import { LcViewToggle } from '../Sections/LcViewToggle';
import { sanitizeText } from '../../utils/text';
import { calendarRange } from '../../utils/date';

export const AdminMyEvents = (props) => {

    let history = useHistory();
    const [events, setevents] = useState([]);
    const localizer = momentLocalizer(moment);
    const [LayoutView, setLayoutView] = useState("List View");
    const [calendarView, setCalendarView] = useState("month");

    const My_Account_button_click = () => {
        if (props.useLoggedIn) history.push('/admin-my-library');
        else props.ShowLoginPopup_();
    };

    // ─── Master stores — full unfiltered API data ─────────────────────────────
    const [allUpcomingEvents, setAllUpcomingEvents] = useState([]);
    const [allPastEvents, setAllPastEvents] = useState([]);

    // ─── Display stores — filtered by local search ────────────────────────────
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [pastEvents, setPastEvent] = useState([]);

    // ─── Separate loaders per section ─────────────────────────────────────────
    const [upcomingLoading, setUpcomingLoading] = useState(true);
    const [pastLoading, setPastLoading] = useState(true);

    const [calendarPopup, setCalendarPopup] = useState(false);
    const [googleLink, setGoogleLink] = useState('');
    const [yahooLink, setYahooLink] = useState('');
    const [outlookLink, setOutlookLink] = useState('');
    const [icalLink, setIcalLink] = useState('');

    // ─── Calendar toolbar ─────────────────────────────────────────────────────
    const CustomToolbar = (tbProps) => <CalendarToolbar {...tbProps} togglePrefix="me" />;
    const EventComponent = ({ event }) => <>{event.title}</>;

    const DateCellWrapperComponent = ({ children, value }) => {
        const cellDate = new Date(value);
        const hasEvent = events.some(e => {
            const d = new Date(e.start);
            return d.getFullYear() === cellDate.getFullYear() &&
                d.getMonth() === cellDate.getMonth() &&
                d.getDate() === cellDate.getDate();
        });
        return (
            <div className={`lc-cal-cell-wrapper${hasEvent ? ' lc-cal-has-event' : ''}`}>
                {children}
                {hasEvent && <span className="lc-cal-event-dot" />}
            </div>
        );
    };

    const openCalendarPopUp = (program_name = '', start_date = '', total_hours = 0) => {
        const event = {
            title: program_name,
            description: "",
            start: start_date,
            duration: [total_hours, "hour"],
        };
        const google_link = google(event);
        const outlook_event = outlook(event);
        office365(event);
        const yahoo_link = yahoo(event);
        const ical_event = ics(event);
        setGoogleLink(google_link);
        setYahooLink(yahoo_link);
        setOutlookLink(outlook_event);
        setIcalLink(ical_event);
        window.location.href = ical_event;
    };

    // ─── Search state ─────────────────────────────────────────────────────────
    const [upcomingSearch, setUpcomingSearch] = useState('');
    const [pastSearch, setPastSearch] = useState('');

    // ─── User state ───────────────────────────────────────────────────────────
    const [userEmail, set_user_email] = useState('');
    const [userToken, setUserToken] = useState(undefined);
    const [userLastname, set_user_lastname] = useState('');

    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const location = useLocation();
    const routeLabels = {
        'admin-profile': 'My Profile',
        'admin-my-events': 'Upcoming Events',
        'admin-my-library': 'My Digital Library',
        'admin-cpd-points': 'CPD Points',
        'my-wishlist': 'Bookmarks',
        'admin-orders': 'Billing History',
    };
    const activeLabel = routeLabels[location.pathname.replace('/', '')] || 'Menu';

    // ─── Local upcoming search — instant, no API call ─────────────────────────
    useEffect(() => {
        if (upcomingSearch.trim() === '') {
            setUpcomingEvents(allUpcomingEvents);
        } else {
            const q = upcomingSearch.toLowerCase();
            setUpcomingEvents(
                allUpcomingEvents.filter(e =>
                    (e.program_name ?? '').toLowerCase().includes(q) ||
                    (e.product_record_type_name ?? '').toLowerCase().includes(q)
                )
            );
        }
    }, [upcomingSearch, allUpcomingEvents]);

    // ─── Local past search — instant, no API call ─────────────────────────────
    useEffect(() => {
        if (pastSearch.trim() === '') {
            setPastEvent(allPastEvents);
        } else {
            const q = pastSearch.toLowerCase();
            setPastEvent(
                allPastEvents.filter(e =>
                    (e.program_name ?? '').toLowerCase().includes(q) ||
                    (e.product_record_type_name ?? '').toLowerCase().includes(q)
                )
            );
        }
    }, [pastSearch, allPastEvents]);

    // ─── Populate user state from props ───────────────────────────────────────
    useEffect(() => {
        if (props.loggedInUserData !== undefined && props.loggedInUserData !== null) {
            set_user_email(props.loggedInUserData.email);
            set_user_lastname(props.loggedInUserData.lastName);
            setUserToken(props.loggedInUserData.accessToken);
        }
    }, [props.loggedInUserData]);

    // ─── Single API call — sets both master stores, loaders independent ───────
    const getData = useCallback(async function getData() {
        if (userEmail === '' || userLastname === '') return;

        setUpcomingLoading(true);
        setPastLoading(true);

        try {            

            const response = await axios.post(
                `${props.EngineURL}salesforce-query/checkout/leo_events.php`,
                {
                    contact_id: userEmail,
                    lname:      userLastname,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${userToken}`,
                    },
                }
            );

            if (response.data.error !== undefined && response.data.error !== '') {
            } else {
                const upcoming = response.data.program_registrations?.upcoming ?? [];
                const past = response.data.program_registrations?.past ?? [];

                // Store master data — search filters via useEffect above
                setAllUpcomingEvents(upcoming);
                setAllPastEvents(past);

                // Set display data immediately
                setUpcomingEvents(upcoming);
                setPastEvent(past);
                UpdateCalendarEvent(upcoming);
            }
        } catch (error) {
        } finally {
            setUpcomingLoading(false);
            setPastLoading(false);
        }
    },[props.EngineURL, userEmail, userLastname, userToken]);

    // ─── Fetch once when token is ready ───────────────────────────────────────
    useEffect(() => {
        getData();
    }, [userToken, getData]);

    // ─── Calendar event list ──────────────────────────────────────────────────
    const UpdateCalendarEvent = (myEvents) => {
        var event_list = [];
        for (var s = 0; s < myEvents.length; s++) {
            if (myEvents[s].program_start_date !== undefined && myEvents[s].program_start_date !== null) {
                event_list.push({
                    ...calendarRange(myEvents[s].program_start_date, myEvents[s].program_end_date),
                    url: "https://store.leocussen.edu.au/product/" + myEvents[s].program_slug,
                    title: sanitizeText(myEvents[s].program_name)
                })

            }
        }
        setevents(event_list);
    };

    const EventClick = (event) => { window.location.href = event.url; };

    // ─── Logout ───────────────────────────────────────────────────────────────
    const doLogout = () => {
        axios.post(
            props.EngineURL + "create-account/2fa/delete_2fa.php",
            { email: props.UpdateloginUserDetail.email },
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${userToken}`,
                },
            }
        )
            .then(function (response) {
                localStorage.removeItem("userDetail");
                localStorage.setItem("isLoggedin", false);
                localStorage.removeItem("checkout_token");
                localStorage.removeItem("thank_you_image");
                localStorage.removeItem("checkout_id");
                localStorage.removeItem("cart_session");
                localStorage.removeItem("create_account_detail");
                localStorage.removeItem("cart_session_sku");
                localStorage.removeItem("isLoggedin");
                localStorage.removeItem("was_book");
                localStorage.removeItem("wishlist_item");
                window.location.href = "/";
            })
            .catch(function (error) {
                console.log(error);
            });
    };

    // ─── Date helpers ─────────────────────────────────────────────────────────
    function formatAMPM(date) {
        var hours = date.getHours();
        var minutes = date.getMinutes();
        var ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12;
        minutes = minutes < 10 ? '0' + minutes : minutes;
        return hours + ':' + minutes + ' ' + ampm;
    }

    function formateDateTime(date) {
        var year = date.getFullYear();
        var month = ('0' + (date.getMonth() + 1)).slice(-2);
        var day = date.getDate();
        var hours = ('0' + date.getHours()).slice(-2);
        var minutes = ('0' + date.getMinutes()).slice(-2);
        var seconds = ('0' + date.getSeconds()).slice(-2);
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    function timeDiffCalc(dateFuture, dateNow) {
        let diffInMilliSeconds = Math.abs(dateFuture - dateNow) / 1000;
        const days = Math.floor(diffInMilliSeconds / 86400);
        diffInMilliSeconds -= days * 86400;
        return days * 24;
    }

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <div className="admin_page admin_events">
                <Helmet>
                    <title>My Events | Leo Cussen Centre for Law</title>
                </Helmet>
                <Container>
                    {props.useLoggedIn !== undefined && props.useLoggedIn === true
                        ? (
                            <div className="admin_wrapper position-relative">
                                {props.loggedInUserData !== null && props.loggedInUserData !== undefined
                                    ? <div className='admin_details_block'>
                                        <p className="ae-greeting-text">{props.Good},</p>
                                        <h1 className="ae-greeting-name">{props.loggedInUserData.firstName}</h1>
                                    </div>
                                    : null
                                }
                                <Row>
                                    <Col xl={3} lg={4} xs={12} className="d-none d-lg-block">
                                        <AdminSidebar doLogout={doLogout} />
                                    </Col>
                                    <Col xl={9} lg={8} xs={12}>
                                        <div className="admin_right_content_block">

                                            {/* ── Disclaimer ──────────────────────────────────────── */}
                                            <>
                                                <div className="ae-disclaimer">
                                                    <div className="ae-disclaimer-icon"><img src={DisclaimerSvg} alt="" /></div>
                                                    <div className="ae-disclaimer-body">
                                                        <p className="ae-disclaimer-title">Disclaimer</p>
                                                        <p className="ae-disclaimer-text">CPD customers please note that past event history is retained for the current and previous CPD years (1 April – 31 March). For enquiries contact <a href="mailto:registration@leocussen.edu.au">registration@leocussen.edu.au</a>.</p>
                                                    </div>
                                                </div>

                                                {/* ── Upcoming Events ───────────────────────────────── */}
                                                <div className="admin_events_list_card position-relative">
                                                    <div className="admin_head d-flex flex-wrap align-items-center justify-content-between">
                                                        <h4 className="m-0">My Upcoming Events</h4>
                                                        {/* Search is instant/local — no submit needed */}
                                                        <Form className="ae-search-form" onSubmit={(e) => e.preventDefault()}>
                                                            <div className="ae-search-group">
                                                                <span className="ae-search-icon"><SearchIcon /></span>
                                                                <FormControl
                                                                    type="search"
                                                                    placeholder="Start typing"
                                                                    className="ae-search-input"
                                                                    aria-label="Search upcoming events"
                                                                    value={upcomingSearch}
                                                                    onChange={(e) => setUpcomingSearch(e.target.value)}
                                                                />
                                                            </div>
                                                        </Form>
                                                    </div>
                                                    <div className="ae-view-toggle-block">
                                                        <div className="ae-view-toggle-btns">
                                                            <button type="button" className={LayoutView === 'List View' ? 'ae-view-btn ae-view-active' : 'ae-view-btn'} onClick={() => setLayoutView('List View')}>List View</button>
                                                            <button type="button" className={LayoutView === 'Calendar View' ? 'ae-view-btn ae-view-active' : 'ae-view-btn'} onClick={() => setLayoutView('Calendar View')}>Calendar View</button>
                                                        </div>
                                                        {LayoutView !== "List View" && (
                                                            <LcViewToggle
                                                                id="me-ext-toggle"
                                                                view={calendarView}
                                                                items={[
                                                                    { label: "Month", id: "me-toggle-month", view: "month" },
                                                                    { label: "Week", id: "me-toggle-week", view: "week" },
                                                                    { label: "Day", id: "me-toggle-day", view: "day" },
                                                                    { label: "Agenda", id: "me-toggle-agenda", view: "agenda" },
                                                                ]}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="all_program_page">
                                                        <Calendar
                                                            localizer={localizer}
                                                            onSelectEvent={(event) => EventClick(event)}
                                                            startAccessor="start"
                                                            endAccessor="end"
                                                            style={{ height: 800 }}
                                                            className={LayoutView === "List View" ? 'd-none' : ''}
                                                            events={events}
                                                            popup
                                                            key="mycalendar"
                                                            onView={setCalendarView}
                                                            step={60}
                                                            timeslots={1}
                                                            min={new Date(0, 0, 0, 7, 0, 0)}
                                                            max={new Date(0, 0, 0, 20, 0, 0)}
                                                            formats={{
                                                                dayFormat: 'ddd',
                                                                dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                                                                    localizer.format(start, 'MMMM YYYY', culture),
                                                                agendaDateFormat: 'ddd D MMM YYYY',
                                                                agendaTimeFormat: 'h:mm A',
                                                                agendaHeaderFormat: ({ start, end }, culture, localizer) =>
                                                                    localizer.format(start, 'MMMM D', culture) + ' – ' + localizer.format(end, 'MMMM D', culture),
                                                            }}
                                                            components={{
                                                                toolbar: CustomToolbar,
                                                                event: EventComponent,
                                                                dateCellWrapper: DateCellWrapperComponent,
                                                            }}
                                                        />
                                                    </div>
                                                    <div className={LayoutView !== "List View" ? 'd-none' : ''}>
                                                        {upcomingEvents.length > 0
                                                            ? upcomingEvents.map((past_event_item) => {
                                                                let program_name = sanitizeText(past_event_item['program_name']);
                                                                let program_start_date_time = formateDateTime(new Date(past_event_item.program_start_date));
                                                                let hours_diff = timeDiffCalc(new Date(past_event_item.program_start_date), new Date(past_event_item.program_end_date));
                                                                let program_slug = past_event_item['program_slug'] !== null && past_event_item['program_slug'] !== undefined ? past_event_item['program_slug'] : '';
                                                                let order_online_number = past_event_item['Shopify_Order_Number__c'];
                                                                let registration_date = past_event_item['Registration_Date__c'] !== undefined && past_event_item['Registration_Date__c'] !== null ? past_event_item['Registration_Date__c'].split('-').reverse().join('/') : null;
                                                                let live_stream = past_event_item['Live_Stream__c'];
                                                                let venue = past_event_item['program_venue'];
                                                                let attendees = past_event_item['attendees'];
                                                                let program_downloads = past_event_item['program_downloads'];
                                                                let program_downloads_keys = Object.keys(program_downloads);
                                                                let program_downloads_length = Object.keys(program_downloads).length;
                                                                let program_date = past_event_item.program_start_date_format2 !== undefined && past_event_item.program_start_date_format2 !== null ? past_event_item.program_start_date_format2 : '';
                                                                let program_time = past_event_item.program_start_time_format2 !== undefined && past_event_item.program_start_time_format2 !== null ? past_event_item.program_start_time_format2 : null;
                                                                let zoom_url = past_event_item.Join_Zoom_Webinar_URL__c !== undefined && past_event_item.Join_Zoom_Webinar_URL__c !== null && past_event_item.Join_Zoom_Webinar_URL__c !== '' ? past_event_item.Join_Zoom_Webinar_URL__c : '';
                                                                program_time = formatAMPM(new Date(past_event_item.program_start_date)).toUpperCase();
                                                                return <Card className="ae-event-card" key={program_slug}>
                                                                    <Card.Body className="ae-card-body">
                                                                        <div className="ae-card-left">
                                                                            <div className="ae-card-info">
                                                                                <Link to={'/product/' + program_slug}><h3 className="ae-card-title">{program_name}</h3></Link>
                                                                                <p className="ae-card-datetime">{program_date}{program_time ? ' • ' + program_time : ''}{venue && venue !== 'Live Stream Only' && venue !== null && venue !== '' ? ' • ' + venue : ''}</p>
                                                                            </div>
                                                                            <div className="ae-delivery-badge">
                                                                                {live_stream === true
                                                                                    ? <><img src={VideoSvg} alt="Live Stream" className="ae-video-icon" /><span>Live Stream Access</span></>
                                                                                    : <><img src={LocationSvg} alt="On-site" className="ae-location-icon" /><span>On-site</span></>
                                                                                }
                                                                            </div>
                                                                            {attendees.length > 0 ? <p className="ae-attendees">Attendees: {attendees.join(', ')}</p> : null}
                                                                            {program_downloads_length > 0 ? (
                                                                                <div className="downloadable_material_block">
                                                                                    <p>Downloadable Materials</p>
                                                                                    <ListGroup as="ul">
                                                                                        {program_downloads_keys.map((download_key) => {
                                                                                            let download_detail = program_downloads[download_key];
                                                                                            let download_file_name = download_detail.Name;
                                                                                            let download_file_link = download_detail.NEILON__File_Presigned_URL__c;
                                                                                            return <ListGroup.Item as="li" key={download_key}><a className="download_link_item" target="_blank" rel="noopener noreferrer" href={download_file_link}><DocumentIcon />{download_file_name}</a></ListGroup.Item>;
                                                                                        })}
                                                                                    </ListGroup>
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                        <div className="ae-card-right">
                                                                            <div className="ae-meta-row">
                                                                                <div className="ae-meta-item">
                                                                                    <span className="ae-meta-label">Order No</span>
                                                                                    <span className="ae-meta-value">{order_online_number !== '' && order_online_number !== null && order_online_number !== undefined ? 'R' + order_online_number : ''}</span>
                                                                                </div>
                                                                                <div className="ae-meta-item">
                                                                                    <span className="ae-meta-label">Registered</span>
                                                                                    <span className="ae-meta-value">{registration_date}</span>
                                                                                </div>
                                                                            </div>
                                                                            {live_stream === true && zoom_url !== '' ? <a href={zoom_url} className="ae-join-btn">Join</a> : null}
                                                                            {past_event_item.program_end_date !== undefined && past_event_item.program_end_date !== null && past_event_item.program_start_date !== undefined && past_event_item.program_start_date !== null
                                                                                ? <button onClick={() => openCalendarPopUp(program_name, program_start_date_time, hours_diff)} className="ae-add-calendar-btn">Add to Calendar</button>
                                                                                : null
                                                                            }
                                                                        </div>
                                                                    </Card.Body>
                                                                </Card>;
                                                            })
                                                            : upcomingLoading
                                                                ? <div className="ae-no-events pre_loader text-center" style={{ position: 'relative', top: 0 }}><LoaderIcon /></div>
                                                                : <div className="ae-no-events"><p>No upcoming events found</p></div>
                                                        }
                                                    </div>
                                                </div>
                                            </>

                                            {/* ── Past Events ───────────────────────────────────────── */}
                                            <>
                                                <div className="admin_events_list_card posotion-relative">
                                                    <div className="admin_head d-flex flex-wrap align-items-center justify-content-between">
                                                        <h4 className="m-0">My Past Events</h4>
                                                        {/* Search is instant/local — no submit needed */}
                                                        <Form className="ae-search-form" onSubmit={(e) => e.preventDefault()}>
                                                            <div className="ae-search-group">
                                                                <span className="ae-search-icon"><SearchIcon /></span>
                                                                <FormControl
                                                                    type="search"
                                                                    placeholder="Start typing"
                                                                    className="ae-search-input"
                                                                    aria-label="Search past events"
                                                                    value={pastSearch}
                                                                    onChange={(e) => setPastSearch(e.target.value)}
                                                                />
                                                            </div>
                                                        </Form>
                                                    </div>
                                                    <div className="ae-view-toggle-block">
                                                        <div className="ae-view-toggle-btns">
                                                            <button type="button" className="ae-view-btn ae-view-active">List View</button>
                                                        </div>
                                                    </div>
                                                    {pastEvents.length > 0
                                                        ? pastEvents.map((past_event_item) => {
                                                            let program_name = sanitizeText(past_event_item['program_name']);
                                                            let program_slug = past_event_item['program_slug'] !== null && past_event_item['program_slug'] !== undefined ? past_event_item['program_slug'] : '';
                                                            let order_online_number = past_event_item['Shopify_Order_Number__c'];
                                                            let registration_date = past_event_item['Registration_Date__c'] !== undefined && past_event_item['Registration_Date__c'] !== null ? past_event_item['Registration_Date__c'].split('-').reverse().join('/') : null;
                                                            let live_stream = past_event_item['Live_Stream__c'];
                                                            let venue = past_event_item['program_venue'];
                                                            let attendees = past_event_item['attendees'];
                                                            let program_downloads = past_event_item['program_downloads'];
                                                            let program_downloads_keys = Object.keys(program_downloads);
                                                            let program_downloads_length = Object.keys(program_downloads).length;
                                                            let program_date = past_event_item.program_start_date_format2 !== undefined && past_event_item.program_start_date_format2 !== null ? past_event_item.program_start_date_format2 : '';
                                                            let program_time = past_event_item.program_start_time_format2 !== undefined && past_event_item.program_start_time_format2 !== null ? past_event_item.program_start_time_format2 : null;
                                                            let zoom_url = past_event_item.Join_Zoom_Webinar_URL__c !== undefined && past_event_item.Join_Zoom_Webinar_URL__c !== null && past_event_item.Join_Zoom_Webinar_URL__c !== '' ? past_event_item.Join_Zoom_Webinar_URL__c : '';
                                                            program_time = formatAMPM(new Date(past_event_item.program_start_date)).toUpperCase();
                                                            return <Card className="ae-event-card" key={program_slug}>
                                                                <Card.Body className="ae-card-body">
                                                                    <div className="ae-card-left">
                                                                        <div className="ae-card-info">
                                                                            <Link to={'/product/' + program_slug}><h3 className="ae-card-title">{program_name}</h3></Link>
                                                                            <p className="ae-card-datetime">{program_date}{program_time ? ' • ' + program_time : ''}{venue && venue !== 'Live Stream Only ' && venue !== null && venue !== '' ? ' • ' + venue : ''}</p>
                                                                        </div>
                                                                        <div className="ae-delivery-badge">
                                                                            {live_stream === true
                                                                                ? <><img src={VideoSvg} alt="Live Stream" className="ae-video-icon" /><span>Live Stream Access</span></>
                                                                                : <><img src={LocationSvg} alt="On-site" className="ae-location-icon" /><span>On-site</span></>
                                                                            }
                                                                        </div>
                                                                        {attendees.length > 0 ? <p className="ae-attendees">Attendees: {attendees.join(', ')}</p> : null}
                                                                        {program_downloads_length > 0 ? (
                                                                            <div className="downloadable_material_block">
                                                                                <p>Downloadable Materials</p>
                                                                                <ListGroup as="ul">
                                                                                    {program_downloads_keys.map((download_key) => {
                                                                                        let download_detail = program_downloads[download_key];
                                                                                        let download_file_name = download_detail.Name;
                                                                                        let download_file_link = download_detail.NEILON__File_Presigned_URL__c;
                                                                                        return <ListGroup.Item as="li" key={download_key}><a className="download_link_item" target="_blank" rel="noopener noreferrer" href={download_file_link}><DocumentIcon />{download_file_name}</a></ListGroup.Item>;
                                                                                    })}
                                                                                </ListGroup>
                                                                            </div>
                                                                        ) : null}
                                                                    </div>
                                                                    <div className="ae-card-right">
                                                                        <div className="ae-meta-row">
                                                                            <div className="ae-meta-item">
                                                                                <span className="ae-meta-label">Order No</span>
                                                                                <span className="ae-meta-value">{order_online_number !== '' && order_online_number !== null && order_online_number !== undefined ? order_online_number : ''}</span>
                                                                            </div>
                                                                            <div className="ae-meta-item">
                                                                                <span className="ae-meta-label">Registered</span>
                                                                                <span className="ae-meta-value">{registration_date}</span>
                                                                            </div>
                                                                        </div>
                                                                        {live_stream === true && zoom_url !== '' ? <a href={zoom_url} target="_blank" rel="noopener noreferrer" className="ae-join-btn">Join</a> : null}
                                                                        {/* No "Add to Calendar" for past events — they've already occurred. */}
                                                                    </div>
                                                                </Card.Body>
                                                            </Card>;
                                                        })
                                                        : pastLoading
                                                            ? <div className="ae-no-events pre_loader text-center" style={{ position: 'relative', top: 0 }}><LoaderIcon /></div>
                                                            : <div className="ae-no-events"><p>No past events found</p></div>
                                                    }
                                                </div>
                                            </>

                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        )
                        : (
                            <div className="text-center">
                                <h3>Please Login</h3>
                                <button onClick={() => My_Account_button_click()} className="btn btn-primary rounded-pill m-0 stest"> Login</button>
                            </div>
                        )
                    }
                </Container>

                {/* ── Add to Calendar modal ──────────────────────────────────────── */}
                <>
                    <Modal show={calendarPopup} onHide={() => setCalendarPopup(false)} className="share_modal">
                        <Modal.Header closeButton className="border-0" />
                        <Modal.Body>
                            <Modal.Title className="text-center">Add To Your Calendar</Modal.Title>
                            <ListGroup horizontal as="ul" className="social_media_list justify-content-center">
                                <ListGroup.Item as="li" className="social_item">
                                    <a target="_blank" rel="noopener noreferrer" href={googleLink}><GoogleIcon fill="#004e6d" /></a>
                                </ListGroup.Item>
                                <ListGroup.Item as="li" className="social_item">
                                    <a target="_blank" rel="noopener noreferrer" href={yahooLink}><YahooIcon fill="#004e6d" /></a>
                                </ListGroup.Item>
                                <ListGroup.Item as="li" className="social_item">
                                    <a target="_blank" rel="noopener noreferrer" href={outlookLink}><OutlookIcon fill="#004e6d" /></a>
                                </ListGroup.Item>
                                <ListGroup.Item as="li" className="social_item">
                                    <a target="_blank" rel="noopener noreferrer" href={icalLink}><ICalendarIcon fill="#004e6d" /></a>
                                </ListGroup.Item>
                            </ListGroup>
                        </Modal.Body>
                    </Modal>
                </>
            </div>

            {/* ── Mobile sidebar ─────────────────────────────────────────────────── */}
            <div className={`admin_sidebar_mobile${mobileSidebarOpen ? ' sidebar-open' : ''}`}>
                <div className="admin_sidebar_drawer">
                    <AdminSidebar doLogout={doLogout} />
                </div>
                <button
                    className="admin_sidebar_trigger"
                    onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
                    aria-expanded={mobileSidebarOpen}
                >
                    <span className="admin_sidebar_trigger_label">{activeLabel}</span>
                    <svg className="admin_sidebar_trigger_dots" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="5" r="1.5" fill="#1C1C1C" />
                        <circle cx="12" cy="12" r="1.5" fill="#1C1C1C" />
                        <circle cx="12" cy="19" r="1.5" fill="#1C1C1C" />
                    </svg>
                </button>
            </div>
        </>
    );
};
